import { API_URL, authenticatedFetch } from "../config/api";

/**
 * Effective-access (RBAC) client.
 *
 * The backend is the authoritative gate; these payloads exist so the UI can
 * avoid offering actions that would 403. Never treat them as security.
 *
 * Note the JSON is camelCase — the backend serializes with a camel alias
 * generator, unlike most other endpoints in this codebase.
 */

/** Global (Keycloak-derived) role. Null when the token carries none of the four. */
export type GlobalRole =
  | 'system-admin'
  | 'internal-user'
  | 'external-contractor'
  | 'customer-user';

/**
 * Project role value as stored in the DB. Only these seven are seeded, but the
 * permission layer keys several more, so unknown strings are allowed through.
 */
export type ProjectRole =
  | 'DEVELOPER_ADMIN'
  | 'DEVELOPER_MANAGER'
  | 'AGENCY_ADMIN'
  | 'AGENCY_MANAGER'
  | 'CLIENT_ADMIN'
  | 'CLIENT_MANAGER'
  | 'CONTRACTOR'
  | (string & {});

/**
 * Display-only label derived from the project role. Null for roles that have no
 * title mapping (CONTRACTOR, DEVELOPER_*) even though they do have permissions,
 * so never gate on this.
 */
export type ProjectRoleTitle =
  | 'Project Owner'
  | 'Project Manager'
  | 'Project Admin'
  | 'Client Admin'
  | 'Client Manager';

/**
 * Permission strings the backend can emit. `*` is the superuser wildcard and
 * `<module>:*` grants every action in that module.
 */
export type Permission =
  | '*'
  | 'project:read'
  | 'project:update'
  | 'project:create'
  | 'project:list_accessible'
  | 'members:manage'
  | 'documents:*'
  | 'documents:read'
  | 'documents:create'
  | 'tasks:*'
  | 'tasks:read'
  | 'tasks:update'
  | 'comments:create'
  | 'approvals:create'
  | 'daily_updates:create'
  | (string & {});

/** Effective access for the calling user on a single project. */
export interface ProjectAccess {
  projectId: string;
  projectName: string;
  projectRole: ProjectRole | null;
  projectRoleTitle: ProjectRoleTitle | null;
  permissions: Permission[];
  modules: Record<string, string[]>;
  /**
   * Flat map of granted permission string -> true. Not expanded: a holder of
   * `documents:*` has `can['documents:*']` but NOT `can['documents:create']`.
   * Prefer `hasPermission` in helpers/projectPermissions, which handles wildcards.
   */
  can: Record<string, boolean>;
  version: number;
}

/**
 * Fetch the current user's effective access for one project.
 *
 * @param projectId Project UUID.
 * @returns The caller's roles, permissions and display title for that project.
 */
export const getProjectAccess = async (projectId: string): Promise<ProjectAccess> => {
  const r = await authenticatedFetch(`${API_URL}/projects/${projectId}/access`);

  if (!r.ok) {
    const text = await r.text();
    throw new Error(text || `Failed to load project access (${r.status})`);
  }

  return r.json();
};

/** Project-independent effective access: the caller's global role and every project they can see. */
export interface MyAccess {
  userId: string;
  keycloakUserId: string | null;
  globalRole: GlobalRole | null;
  globalPermissions: Permission[];
  projects: ProjectAccess[];
  version: number;
}

/**
 * Fetch the current user's global access context — no project required.
 *
 * The source of truth for `globalRole` (e.g. `system-admin`): the backend
 * resolves it from Keycloak roles via an alias table (a raw Keycloak role like
 * `agency-admin` normalizes to `system-admin`) that only exists server-side —
 * don't try to re-derive it from `keycloak.tokenParsed` on the frontend.
 */
export const getMyAccess = async (): Promise<MyAccess> => {
  const r = await authenticatedFetch(`${API_URL}/me/access`);

  if (!r.ok) {
    const text = await r.text();
    throw new Error(text || `Failed to load access context (${r.status})`);
  }

  return r.json();
};
